/** 2 Hours Trading — separate rules from Normal Trading (do not import trading-rules.js). */

export const POOL_CAP = 10000;
export const MAX_POSITIONS = 7;
export const SLOT_CAPITAL = Math.round((POOL_CAP / MAX_POSITIONS) * 100) / 100;
export const MAX_SHARE_PRICE = SLOT_CAPITAL;
export const PER_SHARE_PROFIT = 0.2;
export const MAX_HOLD_MINUTES = 120;
export const MIN_WIN_SCORE = 70;
export const MAX_WIN_SCORE = 90;

/** @deprecated use computeSlotQty — kept for older UI references */
export const MANDATORY_QTY = 1;

export function computeSlotQty(price, budget = SLOT_CAPITAL) {
  const px = Number(price);
  if (!px || px <= 0) return 0;
  return Math.max(1, Math.floor(budget / px));
}

export function getLiveMove(entryPrice, livePrice) {
  if (entryPrice == null || livePrice == null) return null;
  return Math.round((Number(livePrice) - Number(entryPrice)) * 100) / 100;
}

export function isProfitSellHit(entryPrice, livePrice) {
  const move = getLiveMove(entryPrice, livePrice);
  return move != null && move >= PER_SHARE_PROFIT - 0.004;
}

export function isTimedOut(openedAt, maxMin = MAX_HOLD_MINUTES) {
  if (!openedAt) return false;
  const ms = Date.now() - new Date(openedAt).getTime();
  return ms >= maxMin * 60 * 1000;
}

export function formatHoldRemaining(openedAt, maxMin = MAX_HOLD_MINUTES) {
  if (!openedAt) return null;
  const maxMs = maxMin * 60 * 1000;
  const holdMs = Math.max(0, Date.now() - new Date(openedAt).getTime());
  const left = Math.max(0, maxMs - holdMs);
  const m = Math.floor(left / 60000);
  const s = Math.floor((left % 60000) / 1000);
  const expired = left <= 0;
  return {
    expired,
    label: expired ? '2 hr passed' : `${m}m ${s}s left`,
    remainingMs: left
  };
}

export function formatHoldElapsed(openedAt) {
  if (!openedAt) return null;
  const holdMs = Math.max(0, Date.now() - new Date(openedAt).getTime());
  const m = Math.floor(holdMs / 60000);
  const s = Math.floor((holdMs % 60000) / 1000);
  return m > 0 ? `Held ${m}m ${s}s` : `Held ${s}s`;
}

export function meetsWinBand(score) {
  const s = Number(score);
  return s >= MIN_WIN_SCORE && s <= MAX_WIN_SCORE;
}

export function getFixedBotPrice(position) {
  if (!position) return null;
  return position.fixedBotPrice ?? position.entryPrice ?? position.botPrice ?? null;
}

export function classifySellTab(position, livePrice) {
  const entry = getFixedBotPrice(position);
  if (entry == null) return { sell: false };
  if (isProfitSellHit(entry, livePrice)) {
    const move = getLiveMove(entry, livePrice);
    return {
      sell: true,
      kind: 'profit',
      reason: `+₹${(move ?? 0).toFixed(2)}/sh ≥ ₹${PER_SHARE_PROFIT} — sell on Groww now`
    };
  }
  return { sell: false };
}

export function classifyHoldTab(position, livePrice) {
  const entry = getFixedBotPrice(position);
  if (entry == null) return { hold: false };
  const sellInfo = classifySellTab(position, livePrice);
  if (sellInfo.sell) return { hold: false };
  const holdLeft = formatHoldRemaining(position.openedAt);
  const move = getLiveMove(entry, livePrice);
  if (holdLeft?.expired) {
    return {
      hold: true,
      label: '2 HR DONE',
      reason: move != null && move > 0
        ? `2 hr passed · +₹${move.toFixed(2)}/sh — keep holding for +₹${PER_SHARE_PROFIT}/sh (no stop)`
        : `2 hr passed — keep holding for +₹${PER_SHARE_PROFIT}/sh (no stop loss)`
    };
  }
  return {
    hold: true,
    label: 'HOLD',
    reason: move != null && move > 0
      ? `Within 2 hr · +₹${move.toFixed(2)}/sh — wait for +₹${PER_SHARE_PROFIT}/sh`
      : `Within 2 hr window — wait for +₹${PER_SHARE_PROFIT}/sh (no stop loss)`
  };
}

export function classifyBuyTab({ positionsCount = 0, maxPositions = MAX_POSITIONS, topPick }) {
  if (positionsCount >= maxPositions) {
    return { buy: false, reason: `All ${maxPositions} slots filled — hold for +₹${PER_SHARE_PROFIT}/sh` };
  }
  if (!topPick?.symbol) {
    return { buy: false, reason: 'Scanning for 70–90% win stocks…' };
  }
  const score = topPick.rs1PredictScore ?? topPick.winRate ?? topPick.score;
  if (!meetsWinBand(score)) {
    return { buy: false, reason: `Waiting for win rate 70–90% (now ${score ?? '—'}%)` };
  }
  const slotsLeft = maxPositions - positionsCount;
  return {
    buy: true,
    reason: `Win ${score}% · ~₹${Math.round(SLOT_CAPITAL)} per stock · ${slotsLeft} slot(s) left in ₹${POOL_CAP.toLocaleString('en-IN')} pool`
  };
}
